import * as Enumerable from 'linq';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { catchError, map, Observable, of } from 'rxjs';

import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Injector, Input, OnChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { ContactType, Entity, MobileNumberValidateUtility } from '@jms/model';
import { AvatarModule } from '@jms/ui/ui.avatar';
import { PickerItemModule } from '@jms/ui/ui.picker.item';
import { PickerBaseComponent, PickerFetch } from '@jms/ui/ui.pickers.base';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'sms-recipient-picker',
  templateUrl: './sms.recipient.picker.component.html',
  styleUrls: ['./sms.recipient.picker.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: SmsRecipientPickerProComponent, multi: true },
    { provide: NG_VALIDATORS, useExisting: SmsRecipientPickerProComponent, multi: true }
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgSelectModule,
    PickerItemModule,
    AutoCompleteModule,
    AvatarModule
  ]
})
export class SmsRecipientPickerProComponent extends PickerBaseComponent implements OnChanges, Validator {

  @Input() public placeholder = '<Recipients>';
  @Input() public appendTo;
  @Input() public multiple: boolean;
  @Input() public data: Entity[];
  @Input() public addTag = true;

  public items$: Observable<any[]>;

  constructor(
    injector: Injector,
    private http: HttpClient
  ) {
    super(injector);
  }

  public ngOnChanges() {

    const data$ = this.fetch === PickerFetch.Automatic
      ? this.getRecipients()
      : of(this.data);

    this.items$ = data$
      .pipe(
        map((items: any[]) => {

          return Enumerable
            .from(items)
            .where(e => e.mobile)
            .select(e => {
              const validationResult = MobileNumberValidateUtility.validateNumber(e.mobile);
              e.disabled = !validationResult.valid;
              return e;
            })
            .select(e => {

              switch (e.type) {
                case ContactType.Subcontractor: {
                  e['group'] = 'Contractors';
                  break;
                }
                case ContactType.Business:
                case ContactType.Individual:
                case ContactType.ServiceProvider: {
                  e['group'] = 'Customers';
                  break;
                }
                case ContactType.LoginContact:
                case ContactType.BasicContact: {
                  e['group'] = 'Contacts';
                  break;
                }
                case ContactType.Employee: {
                  e['group'] = 'Employees';
                  break;
                }
                case ContactType.Supplier: {
                  e['group'] = 'Suppliers';
                  break;
                }
              }

              return e;
            })
            .orderBy(e => e['group'])
            .thenBy(e => e?.name)
            .toArray();

        })
      );

  }

  private getRecipients(): Observable<Entity[]> {

    return this.http.get<Entity[]>(`/api/picker/contact/sms/recipient`)
      .pipe(
        map(x => x),
        catchError(error => {
          return of([]);
        })
      );


  }

  public onAddTag(text: string) {
    return {
      mobile: text,
      name: text,
      _id: text,
    }
  }


  // Validator

  validate(control: AbstractControl<any, any>): ValidationErrors {
    const value = control?.value;
    const recipients = [];
    if (value) {
      if (Array.isArray(value)) {
        recipients.push(...value);
      }
      else {
        recipients.push(value);
      }
    }

    const invalidNumbers = recipients.filter(r => r.disabled || MobileNumberValidateUtility.validateNumber(r.mobile).valid === false);

    if (invalidNumbers.length > 0) {
      return {
        invalidNumbers: invalidNumbers.map(r => r.mobile).join(', ')
      };
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }

  onValidationChange = () => { };

  public getDisplayValue(item: any): string {
    if (!item) {
      return '';
    }

    if (item.mobile === item.name) {
      return item.mobile;
    }

    return `${item.name} (${item.mobile})`;
  }

}

